import { useCallback, useEffect, useState } from 'react';
import { dashboardGateway } from '../../dashboard-v2/index';
import { taxFilingsGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { sandboxTransmissionActionsEnabled } from '../../../shared/commercialActions';
import { FeatureUnavailableNotice } from '../../../shared/components/FeatureUnavailableNotice';
import {
  taxFormCodeLabels,
  taxFilingDashboardStatusLabels,
  type TaxFilingDashboardCardResponse,
} from '../types';

interface CompanySummaryRow {
  companyId: string;
  companyName: string;
  isActive: boolean;
  notPreparedCount: number;
  inReviewCount: number;
  filedCount: number;
  needsAttentionCount: number;
}

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC' });
}

/**
 * EPN-40 — the Super Admin E-Filing Dashboard: a real, cross-company view of tax-filing status, reserved to
 * a caller holding `platform.taxfilings.read` (SuperAdmin). Reuses EPN-44's existing `dashboardGateway`
 * (`getSuperAdminSummary`) for the company roster/names rather than a second company-listing call, and
 * EPN-41's own `taxFilingsGateway` (`listPlatformSummary`/`listDashboard` with a `companyId` override) for
 * the filing data — no second filing engine.
 *
 * A company absent from the platform-summary response has no filing activity for the tax year at all; it
 * is shown here with all 5 periods Not Prepared, exactly as a brand-new single-company caller would see on
 * their own dashboard.
 *
 * Deliberately READ-ONLY: preparing, approving, or denying a filing on another company's behalf is not part
 * of this pass — the approved plan only extended the two read endpoints, and the approved design itself
 * never showed a mutation action in this cross-company view, only "View." See
 * docs/epn-40-super-admin-partner-efiling-dashboard-gaps.md.
 *
 * There is no Partner view anywhere in this file — no Partner role, client-base relationship, or scoping
 * model exists in this codebase today; see that same doc for the blocked determination. A caller without
 * `platform.taxfilings.read` gets a real 403 from the server, surfaced via `friendlyErrorMessage` exactly
 * like every other restricted screen in this app — there is no client-side permission check gating this
 * page or its nav entry (same precedent as EPN-44's own `SuperAdminDashboardPage`).
 */
export function SuperAdminTaxFilingsDashboardPage() {
  // EPN-98: no live filing provider exists, so every filing status this view could show (Filed, In Review) is simulated. A production bundle says
  // so and fetches nothing; only development and test builds show the sandbox view. See shared/commercialActions.ts.
  return sandboxTransmissionActionsEnabled ? <PlatformTaxFilings /> : (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Super Admin Tax Filings</h2>
      </div>
      <FeatureUnavailableNotice feature="Electronic tax filing">
        EPN is not connected to a live tax-filing provider in this release, so there are no real filings to show across companies. No filing has been
        submitted to any tax agency from EPN.
      </FeatureUnavailableNotice>
    </section>
  );
}

function PlatformTaxFilings() {
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<CompanySummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedCompany, setSelectedCompany] = useState<{ id: string; name: string } | null>(null);
  const [drilldownCards, setDrilldownCards] = useState<TaxFilingDashboardCardResponse[]>([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summary, platformSummary] = await Promise.all([
        dashboardGateway.getSuperAdminSummary(),
        taxFilingsGateway.listPlatformSummary(taxYear),
      ]);
      const byCompanyId = new Map(platformSummary.map((r) => [r.companyId, r]));
      setRows(summary.companies.map((c) => {
        const s = byCompanyId.get(c.id);
        return {
          companyId: c.id,
          companyName: c.name,
          isActive: c.isActive,
          notPreparedCount: s?.notPreparedCount ?? 5,
          inReviewCount: s?.inReviewCount ?? 0,
          filedCount: s?.filedCount ?? 0,
          needsAttentionCount: s?.needsAttentionCount ?? 0,
        };
      }));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load the platform tax filings summary.'));
    } finally {
      setLoading(false);
    }
  }, [taxYear]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  async function openCompany(companyId: string, companyName: string) {
    setSelectedCompany({ id: companyId, name: companyName });
    setDrilldownCards([]);
    setDrilldownLoading(true);
    setDrilldownError(null);
    try {
      setDrilldownCards(await taxFilingsGateway.listDashboard(taxYear, companyId));
    } catch (err) {
      setDrilldownError(friendlyErrorMessage(err, "Could not load this company's tax filings."));
    } finally {
      setDrilldownLoading(false);
    }
  }

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Super Admin Tax Filings</h2>
        <span className="badge badge--caution">Sandbox Simulation Only — No Agency Submission</span>
      </div>
      <p className="muted">
        Cross-company view, reserved to a caller holding <code>platform.taxfilings.read</code> — see{' '}
        <code>docs/epn-40-super-admin-partner-efiling-dashboard-gaps.md</code>. Read-only: preparing,
        approving, or denying a filing on another company's behalf is not part of this pass. No Partner view
        exists — see that same doc for why.
      </p>

      {error && <p className="error">{error}</p>}

      {!selectedCompany && (
        <>
          <label>
            Tax Year
            <input
              data-testid="platform-tax-year-input"
              type="number" min="2020" max="2100" value={taxYear}
              onChange={(e) => setTaxYear(Number(e.target.value))}
            />
          </label>

          {loading && <p className="muted">Loading…</p>}

          {!loading && (
            <table data-testid="platform-company-table">
              <thead>
                <tr>
                  <th>Company</th><th>Not Prepared</th><th>In Review</th><th>Filed</th><th>Needs Attention</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.companyId} data-testid={`platform-company-row-${r.companyId}`}>
                    <td>{r.companyName}{!r.isActive && <span className="muted"> (inactive)</span>}</td>
                    <td>{r.notPreparedCount}</td>
                    <td>{r.inReviewCount}</td>
                    <td>{r.filedCount}</td>
                    <td>{r.needsAttentionCount}</td>
                    <td>
                      <button type="button" className="link" onClick={() => void openCompany(r.companyId, r.companyName)}>View</button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="muted">No companies found.</td></tr>}
              </tbody>
            </table>
          )}
        </>
      )}

      {selectedCompany && (
        <div className="section">
          <div className="section-header">
            <h3>{selectedCompany.name}</h3>
            <button type="button" className="link" onClick={() => setSelectedCompany(null)}>‹ Back to List of Companies</button>
          </div>

          {drilldownError && <p className="error">{drilldownError}</p>}
          {drilldownLoading && <p className="muted">Loading…</p>}

          {!drilldownLoading && !drilldownError && (
            <table data-testid="platform-drilldown-table">
              <thead>
                <tr><th>Form</th><th>Period</th><th>Due Date</th><th>Status</th><th>Prepared By</th><th>Reviewed By</th></tr>
              </thead>
              <tbody>
                {drilldownCards.map((c) => (
                  <tr key={`${c.formCode}-${c.quarter ?? 'annual'}`}>
                    <td>{taxFormCodeLabels[c.formCode]}</td>
                    <td>{c.taxYear}{c.quarter ? ` Q${c.quarter}` : ''}</td>
                    <td>{formatDate(c.dueDate)}</td>
                    <td><span className={`badge badge--status-${c.status.toLowerCase()}`}>{taxFilingDashboardStatusLabels[c.status]}</span></td>
                    <td className="muted">{c.preparedByEmailSnapshot ?? '—'}</td>
                    <td className="muted">{c.reviewedByEmailSnapshot ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
